<template>
    <div class="searcher-container">
        <div class="search-layout">
            <div class="searcher-wrapper">
                <div class="search-bar-wrapper">
                    <div class="search-bar-wrapper-inner">
                        <el-input
                            v-model="searchQuery"
                            type="textarea"
                            :placeholder="animatedPlaceholder"
                            class="search-input black-textarea"
                            id="search-input"
                            @keydown.enter.prevent="submit"
                            :disabled="mainFilters.length >= 10"
                        />
                    </div>
                    <el-button
                        class="search-btn"
                        :type="
                            searchQuery && mainFilters.length < 10
                                ? 'primary'
                                : 'info'
                        "
                        :disabled="!searchQuery || mainFilters.length >= 10"
                        @click="submit"
                        :id="searchButtonId"
                    >
                        Kandidaten suchen
                        <img src="/media/images/web_search/ai_icon_white.svg" />
                    </el-button>
                </div>
            </div>
        </div>

        <div class="icons-wrapper"></div>
    </div>
</template>

<script>
import { mapStores } from "pinia";
import { Loading } from "@element-plus/icons-vue";
import { ElButton, ElInput, ElIcon } from "element-plus";
import { useSearchOptionsStore } from "@/stores/SearchOptionsStore";
import {
    Plus as PlusIcon,
    CirclePlus as CirclePlusIcon,
} from "@element-plus/icons-vue";

export default {
    components: {
        ElButton,
        ElInput,
        ElIcon,
        Loading,
        PlusIcon,
        CirclePlusIcon,
    },
    props: {
        mainFilters: {
            type: Array,
            required: true,
        },
        groupName: String,
    },
    data() {
        return {
            searchQuery: "",
            loading: false,
            tags: [],
            allTags: [],
            showMoreTags: false,
            hierarchyTags: [],
            hierarchyBreadcrumb: [],
            animatedPlaceholder: "",
            typingIndex: 0,
            animationTimeout: null,
        };
    },
    mounted() {
        this.loadOptions();
        this.fetchHierarchiesByLevel(0);
        this.startTypewriterAnimation();
    },
    beforeUnmount() {
        if (this.animationTimeout) {
            clearTimeout(this.animationTimeout);
        }
    },
    computed: {
        ...mapStores(useSearchOptionsStore),
        displayedTags() {
            return this.showMoreTags
                ? this.hierarchyTags
                : this.hierarchyTags.slice(0, 14);
        },
        showMoreText() {
            return this.showMoreTags ? "Weniger anzeigen" : "alle anzeigen";
        },
        searchButtonId() {
            switch (this.groupName) {
                case "Deutsche Interim AG":
                    return "di_search_candidate";
                case "Swiss Interim GmbH":
                    return "si_search_candidate";
                case "Matchmaker":
                    return "mm_search_candidate";
                default:
                    return "di_search_candidate";
            }
        },
    },
    watch: {
        mainFilters() {
            this.SearchOptionsStore.setActiveFilters(this.mainFilters);
            if (this.SearchOptionsStore.getOptions().length) {
                setTimeout(() => this.setOptions(), 2500);
            }
        },
    },
    methods: {
        startTypewriterAnimation() {
            const text = "Beschreiben Sie, wo nach Sie suchen...";
            if (this.typingIndex < text.length) {
                this.animatedPlaceholder += text.charAt(this.typingIndex);
                this.typingIndex++;
                this.animationTimeout = setTimeout(
                    this.startTypewriterAnimation,
                    100,
                );
            } else {
                this.animationTimeout = setTimeout(() => {
                    this.animatedPlaceholder = "";
                    this.typingIndex = 0;
                    this.startTypewriterAnimation();
                }, 2000);
            }
        },
        async submit() {
            if (!this.searchQuery || this.searchQuery.trim().length === 0) {
                return;
            }
            this.loading = true;

            try {
                const res = await fetch("/api/ai/response", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    body: JSON.stringify({
                        question: this.searchQuery,
                        run_id: null,
                        thread_id: null,
                    }),
                });

                const data = await res.json();

                if (data.skill_ids && Array.isArray(data.skill_ids)) {
                    const allOptions = this.SearchOptionsStore.getOptions();

                    const matchedCompetenceIds = data.skill_ids
                        .map((id) => {
                            const value = "competence_" + id;
                            const found = allOptions.find(
                                (opt) => opt.value === value,
                            );
                            return found ? id.toString() : null;
                        })
                        .filter(Boolean);

                    const topTenIds = matchedCompetenceIds.slice(0, 10);

                    if (topTenIds.length > 0) {
                        this.$emit("searchResults", {
                            competence: topTenIds,
                            job_tag: "",
                            modifier: "OR",
                        });
                    }
                }
            } catch (error) {
                console.error("Error downloading skills from backend:", error);
            } finally {
                this.loading = false;
            }
        },
        getRandomTags() {
            const shuffled = this.allTags.sort(() => 0.5 - Math.random());
            return shuffled.slice(
                0,
                Math.max(20, Math.floor(Math.random() * 4) + 5),
            );
        },
        toggleShowTags() {
            this.showMoreTags = !this.showMoreTags;
        },
        async loadOptions() {
            const options = this.fetchOptions();

            options.then((response) => {
                let optionsData = Object.entries(response.results)
                    .map(([idx, option]) => ({
                        value: option.id,
                        label: option.text,
                    }))
                    .filter((option) => option.label);

                optionsData = optionsData.sort((a, b) =>
                    a.label.localeCompare(b.label),
                );
                this.SearchOptionsStore.setOptions(optionsData);

                setTimeout(() => this.setOptions(), 2500);
            });
        },
        async fetchOptions() {
            let pathname = window.location.pathname;
            if (pathname.startsWith("/")) pathname = pathname.substring(1);
            pathname = pathname.split("/");
            let poolLang = pathname.length > 2 ? pathname[2] : "de";

            const response = await fetch(
                "/web-search/options/" + poolLang + "/?format=json",
            );
            return await response.json();
        },
        setOptions() {
            this.value = this.mainFilters.map(
                (option) => option.type + "_" + option.id,
            );
        },
        onTagClick(tagLabel) {
            const allOptions = this.SearchOptionsStore.getOptions();
            const found = allOptions.find((opt) => opt.label === tagLabel);
            if (!found) return;

            const competenceId = found.value.replace(/^competence_/, "");
            this.$emit("searchResults", {
                competence: [competenceId],
                job_tag: "",
                modifier: "OR",
            });
        },
        async fetchHierarchiesByLevel(level) {
            this.loading = true;
            this.hierarchyTags = [];
            try {
                const response = await fetch(
                    `/api/hierarchies/get-by-level?level=${level}`,
                );
                const json = await response.json();
                this.hierarchyTags = json.data || [];
                this.hierarchyBreadcrumb = [];
            } catch (e) {
                console.error("Error loading hierarchy:", e);
            } finally {
                this.loading = false;
            }
        },
        async fetchHierarchiesByParent(parentId) {
            this.loading = true;
            try {
                const response = await fetch(
                    `/api/hierarchies/get-by-parent?parent_id=${parentId}`,
                );
                const json = await response.json();
                this.hierarchyTags = json.data || [];
            } catch (e) {
                console.error("Error loading children:", e);
            } finally {
                this.loading = false;
            }
        },
        async onHierarchyTagClick(tag) {
            this.loading = true;
            try {
                const res = await fetch(
                    `/api/hierarchies/get-skills-by-hierarchy?id=${tag.id}`,
                );
                const data = await res.json();

                this.hierarchyBreadcrumb.push({
                    id: tag.id,
                    label: tag.label || tag.name,
                });

                if (data.skills && data.skills.length > 0) {
                    const allOptions = this.SearchOptionsStore.getOptions();

                    const skillIds = data.skills.map(
                        (skill) => "competence_" + skill.id,
                    );
                    const matchedTags = skillIds
                        .map((id) => {
                            const found = allOptions.find(
                                (opt) => opt.value === id,
                            );
                            return found
                                ? {
                                      label: found.label,
                                      value: found.value,
                                      type: "skill",
                                  }
                                : null;
                        })
                        .filter(Boolean);

                    this.hierarchyTags = matchedTags;
                } else {
                    await this.fetchHierarchiesByParent(tag.id);
                }
            } catch (e) {
                console.error("Error while checking skills:", e);
            } finally {
                this.loading = false;
            }
        },
        async goToBreadcrumb(index) {
            this.hierarchyBreadcrumb = this.hierarchyBreadcrumb.slice(
                0,
                index + 1,
            );
            const last =
                this.hierarchyBreadcrumb[this.hierarchyBreadcrumb.length - 1];
            if (last) {
                await this.fetchHierarchiesByParent(last.id);
            } else {
                await this.fetchHierarchiesByLevel(0);
            }
        },
        async resetHierarchy() {
            this.hierarchyBreadcrumb = [];
            await this.fetchHierarchiesByLevel(0);
        },
        async fakeSkillSearch(query) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve([
                        "competence_4387",
                        "competence_5960",
                        "competence_900",
                    ]);
                }, 1000);
            });
        },
    },
};
</script>

<style scoped>
.search-layout {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
}

.searcher-wrapper {
    justify-content: center;
    width: 100%;
}

.search-description-wrapper {
    padding-top: 25px;
}

.searcher-container {
    background: #cddee2;
    padding: 20px 20px 0;
    border-radius: 12px 12px 12px 12px;
}

.search-bar-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 25px;
    width: 100%;
    gap: 8px;
    flex-wrap: nowrap;
    overflow-x: auto;
}

.search-bar-wrapper-inner {
    flex-grow: 1;
    min-width: 0;
    width: 100%;
}

.search-btn {
    border-radius: 13px;
    padding: 0 30px;
    background-color: #41abc4;
    color: #fff;
    width: 276px;
    height: 64px;
    border: 2px solid #fff;
    text-align: left;
    font: normal 400 20px/24px Barlow;
    white-space: nowrap;
    flex-shrink: 0;
    margin-left: 20px;
    margin-bottom: 0px;
}

.search-btn img {
    width: 24px;
    margin-left: 5px;
    margin-bottom: 23px;
}

.search-with-ai-button {
    margin-left: 15px;
    border-radius: 24px;
    padding: 10px 20px 10px 30px;
    color: #fff;
    border: none;
    cursor: pointer;
    font-size: 16px;
    background: linear-gradient(90deg, #a06dd6, #67a2c5);
    background-size: 200%;
    background-position: 0%;
    transition: background-position 0.3s ease-in-out;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.search-with-ai-button:hover {
    background: #b465d8 !important;
    text-decoration: none;
}

.ai-icon {
    width: 20px;
    height: 20px;
    margin-left: 10px;
}

.el-button--primary {
    --el-button-text-color: var(--el-color-white);
    --el-button-bg-color: #41abc4;
    --el-button-border-color: #41abc4;
    --el-button-hover-bg-color: #2699b2;
    --el-button-hover-border-color: #2699b2;
}

.el-select.search-select {
    width: 500px;
}

.el-input__wrapper,
.el-input:deep(.el-input__wrapper),
.search-input:deep(.el-input__wrapper) {
    border: 1px solid #9c979a;
    width: 100%;
    height: 60px;
    background: #fff;
    border-radius: 13px;
    box-sizing: border-box;
    font: normal 18px/55px Inter;
    color: #696969;
    text-align: left;
}

.el-select:deep(.el-select__caret),
.el-select:deep(.el-icon.el-select__caret),
.el-select:deep(.el-icon.el-select__caret svg) {
    color: #41abc4;
    width: 2em;
    height: 2em;
}

.el-switch,
.el-switch :deep(.el-switch__core) {
    height: 25px;
    border-radius: 20px;
}

.el-switch {
    --el-switch-on-color: #41abc4;
    --el-switch-off-color: #707070;
    margin: 0 20px;
}

.el-switch :deep(.el-switch__inner .is-text) {
    padding: 0 4px;
}

.icons-wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    padding-top: 5pxe;
    height: 40px;
}

.icon-item {
    display: contents;
    flex-direction: column;
    align-items: center;
}

.icon-item img {
    width: 25px;
    height: 63px;
    padding-right: 7px;
}

.icon-item p {
    margin-top: 5px;
    font: normal 400 14px/55px Inter;
    color: #454545;
    text-align: left;
    margin-right: 35px;
}

.header-section {
    font: normal medium 24px/29px Barlow;
    color: #1c1c1c;
    text-align: left;
}

.breadcrumb-section {
    margin-bottom: 20px;
    font: normal 18px/22px Barlow;
    color: #343433;
    text-align: left;
}

.breadcrumb-section a {
    color: #343433;
    text-decoration: underline;
}

.results-section {
    padding: 0;
}

.tags-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.tag-item {
    padding: 11px 15px;
    border-radius: 5px;
    cursor: pointer;
    font: normal 400 20px/24px Barlow;
    color: #3c3c3c;
    background-color: #e1ebee;
    text-align: center;
}

.tag-item.skill:hover {
    color: #fff;
    background-color: #41abc4;
}

.tag-item.skill,
.skill-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    position: relative;
}

.skill-tag .standard,
.skill-tag .hover {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.skill-tag .hover {
    display: none;
}

.tag-item.skill:hover .standard,
.skill-tag:hover .standard {
    display: none;
}

.tag-item.skill:hover .hover,
.skill-tag:hover .hover {
    display: inline-flex;
}

.skill-tag .icon-wrapper {
    width: 23px;
    height: 23px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.skill-tag .standard .icon {
    width: 16px;
    height: 16px;
}

.skill-tag .hover .icon {
    width: 23px;
    height: 23px;
}

.show-more {
    margin-top: 10px;
}

.loading {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: bold;
}

.show-more-arrow {
    position: absolute;
}

.show-more-container {
    padding-right: 30px;
}

:deep(.black-textarea textarea) {
    border: 1px solid #000 !important;
    color: #000 !important;
    resize: vertical !important;
    min-height: 63px;
    font-size: 22px;
    line-height: 1.5;
    border-radius: 13px 13px 13px 13px;
    height: 63px;
    max-width: 100%;
    padding: 13px 12px 12px 12px;
}

:deep(.black-textarea textarea::placeholder) {
    color: #000 !important;
    opacity: 1 !important;
}

:deep(.black-textarea textarea:-ms-input-placeholder) {
    color: #000 !important;
}

:deep(.black-textarea textarea::-ms-input-placeholder) {
    color: #000 !important;
}

/* Responsive */
@media only screen and (max-width: 790px) {
    .searcher-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        min-height: unset !important;
    }

    .search-bar-wrapper {
        flex-direction: column;
        align-items: stretch;
    }

    .search-btn {
        width: 100%;
        justify-content: center;
    }

    .header-section h1 {
        font-size: 20px;
        text-align: center;
    }

    .breadcrumb-section {
        font-size: 12px;
        text-align: center;
    }

    .search-bar-wrapper .el-button {
        margin: 10px 0;
    }

    .search-bar-wrapper .el-switch {
        margin-right: 0;
    }

    .icons-wrapper {
        display: block !important;
    }

    .icon-item {
        flex: 1 1 100%;
    }

    .icons-wrapper {
        flex-direction: column;
        gap: 12px;
    }

    .icon-item {
        display: flex;
        flex-direction: row;
        align-items: center;
    }

    .icon-item img {
        width: 22px;
        height: auto;
    }

    .icon-item p {
        margin: 0;
    }

    .search-input:deep(.el-textarea__inner) {
        min-height: 100px !important;
    }
}

@media only screen and (max-width: 1502px) {
    .icon-item p {
        font: normal 400 9px/26px Inter;
    }

    .icon-item img {
        width: 17px !important;
    }
}
</style>
